// @ts-check
//
// Client-env builders for `next.config.js` (WEB-560 / WEB-562).
// Kept as CommonJS so it can be:
//   1. `require`d from `next.config.js` without a TS compile step; and
//   2. imported by Vitest + `config/env.ts` without pulling next.config side effects.
//
// WEB-562: `CLIENT_ENV_KEYS` is the single source of truth for every
// `NEXT_PUBLIC_*` key that must reach the client bundle via `env: {…}` /
// `getClientEnvVars()`. Adding a new public var means updating this list
// (and the Zod field in `config/env.ts`); the drift test fails if either
// side is forgotten.

/**
 * Ordered list of client-exposed env keys. Keep alphabetical within
 * logical groups only when adding — do not reorder existing keys without
 * updating snapshot-style tests.
 *
 * @type {readonly string[]}
 */
const CLIENT_ENV_KEYS = Object.freeze([
  'NEXT_PUBLIC_API_URL',
  'NEXT_PUBLIC_WS_URL',
  'NEXT_PUBLIC_GEOCORE_URL',
  'NEXT_PUBLIC_GOOGLE_MAPS_API_KEY',
  'NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID',
  'NEXT_PUBLIC_AFFILIATE_CHECKOUT_URL',
  'NEXT_PUBLIC_VAPID_PUBLIC_KEY',
  'NEXT_PUBLIC_ENABLE_ANALYTICS',
  'NEXT_PUBLIC_SHOW_DEV_TOOLS',
  'NEXT_PUBLIC_APP_NAME',
  'NEXT_PUBLIC_PASSKEYS_ENABLED',
  'NEXT_PUBLIC_PASSWORD_AUTH_ENABLED',
  'NEXT_PUBLIC_DEFAULT_CITY_CODE',
  'NEXT_PUBLIC_APEX_BEHAVIOR',
  'NEXT_PUBLIC_LOCI_ROOT_DOMAIN',
  'NEXT_PUBLIC_LOCI_STAGE',
  'NEXT_PUBLIC_ANALYTICS_ID',
  'NEXT_PUBLIC_SENTRY_DSN',
  'NEXT_PUBLIC_POSTHOG_KEY',
  'NEXT_PUBLIC_POSTHOG_HOST',
]);

/**
 * String defaults for the CI / validation-failure fallback path.
 * Booleans are stringified because Next.js `env` inlines strings.
 *
 * @type {Readonly<Record<string, string>>}
 */
const DEFAULT_CLIENT_ENV = Object.freeze({
  NEXT_PUBLIC_API_URL: 'http://localhost:4000',
  // `/graphql` path required — ws-subscription-server binds to that path (FRE-815).
  NEXT_PUBLIC_WS_URL: 'ws://localhost:4001/graphql',
  NEXT_PUBLIC_GEOCORE_URL: 'https://geo.loci.city',
  NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: '',
  NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID: '',
  NEXT_PUBLIC_AFFILIATE_CHECKOUT_URL: 'http://localhost:4200',
  NEXT_PUBLIC_VAPID_PUBLIC_KEY: '',
  NEXT_PUBLIC_ENABLE_ANALYTICS: 'false',
  NEXT_PUBLIC_SHOW_DEV_TOOLS: 'false',
  NEXT_PUBLIC_APP_NAME: 'LOCI',
  // Omit default — schema treats unset as enabled via the hook's own default.
  NEXT_PUBLIC_PASSKEYS_ENABLED: '',
  // Unset stays on (`isPasswordAuthEnabled`). Only the literal 'false' retires it.
  NEXT_PUBLIC_PASSWORD_AUTH_ENABLED: '',
  NEXT_PUBLIC_DEFAULT_CITY_CODE: 'CLT',
  NEXT_PUBLIC_APEX_BEHAVIOR: 'redirect-default',
  NEXT_PUBLIC_LOCI_ROOT_DOMAIN: 'loci.city',
  NEXT_PUBLIC_LOCI_STAGE: '',
  NEXT_PUBLIC_ANALYTICS_ID: '',
  NEXT_PUBLIC_SENTRY_DSN: '',
  NEXT_PUBLIC_POSTHOG_KEY: '',
  NEXT_PUBLIC_POSTHOG_HOST: 'https://p.loci.city',
});

/**
 * Build a fallback env object from process.env with per-context defaults.
 * Used when Zod validation is skipped (CI) or fails.
 *
 * @param {Record<string, unknown>} [overrides]
 * @param {{ isCI?: boolean }} [options]
 */
function buildFallbackEnv(overrides = {}, options = {}) {
  const isCI = options.isCI ?? (process.env.CI === 'true' || process.env.GITHUB_ACTIONS === 'true');

  // Fail loud when a production build is missing NEXT_PUBLIC_API_URL: the
  // localhost fallback would otherwise be inlined into the client bundle and
  // the prod CSP would silently block every browser request to it (this was
  // the source of the 2026-04-09 /events page CSP violation). Dev/test/CI
  // still get the localhost fallback for convenience.
  const isProdBuild =
    process.env.NODE_ENV === 'production' &&
    process.env.NEXT_PHASE === 'phase-production-build' &&
    !isCI;
  if (isProdBuild && !process.env.NEXT_PUBLIC_API_URL) {
    throw new Error(
      'NEXT_PUBLIC_API_URL is required for production builds. Set it in Vercel project settings (build environment) — otherwise the localhost:4000 fallback gets baked into the client bundle and prod CSP blocks every API call.',
    );
  }

  return {
    NODE_ENV: process.env.NODE_ENV || 'development',
    PORT: parseInt(process.env.PORT || '3000', 10),
    LOG_LEVEL: process.env.LOG_LEVEL || 'info',
    CORS_ORIGIN: process.env.CORS_ORIGIN || 'http://localhost:3000',
    NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL || DEFAULT_CLIENT_ENV.NEXT_PUBLIC_API_URL,
    NEXT_PUBLIC_WS_URL: process.env.NEXT_PUBLIC_WS_URL || DEFAULT_CLIENT_ENV.NEXT_PUBLIC_WS_URL,
    NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID:
      process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID ||
      DEFAULT_CLIENT_ENV.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID,
    AI_MODEL_REASONING: process.env.AI_MODEL_REASONING || 'gpt-4o',
    AI_MODEL_FAST: process.env.AI_MODEL_FAST || 'gpt-4o-mini',
    AI_MODEL_CODE: process.env.AI_MODEL_CODE || 'claude-sonnet-4-20250514',
    AI_MODEL_TOOLS: process.env.AI_MODEL_TOOLS || 'gpt-4o',
    ...overrides,
  };
}

/**
 * Resolve a single client env value for the fallback (string) path.
 *
 * @param {Record<string, unknown> | undefined} env
 * @param {string} key
 * @returns {string}
 */
function resolveClientEnvString(env, key) {
  const fromValidated = env?.[key];
  if (fromValidated !== undefined && fromValidated !== null && fromValidated !== '') {
    return String(fromValidated);
  }
  const fromProcess = process.env[key];
  if (fromProcess !== undefined && fromProcess !== '') {
    return fromProcess;
  }
  return DEFAULT_CLIENT_ENV[key] ?? '';
}

/**
 * Build the Next.js `env: {…}` object for the fallback path (CI / failed Zod).
 * Every key in `CLIENT_ENV_KEYS` is emitted so the client bundle receives an
 * identical key set to the validated `getClientEnvVars()` path.
 *
 * @param {Record<string, unknown>} [env]
 * @returns {Record<string, string>}
 */
function buildClientEnv(env = {}) {
  /** @type {Record<string, string>} */
  const out = {};
  for (const key of CLIENT_ENV_KEYS) {
    out[key] = resolveClientEnvString(env, key);
  }
  return out;
}

/**
 * Pick client-safe values from a validated env object (Zod output).
 * Used by `getClientEnvVars()` in `config/env.ts`.
 *
 * @param {Record<string, unknown>} env
 * @returns {Record<string, unknown>}
 */
function pickClientEnvVars(env) {
  /** @type {Record<string, unknown>} */
  const out = {};
  for (const key of CLIENT_ENV_KEYS) {
    out[key] = env[key];
  }
  return out;
}

module.exports = {
  CLIENT_ENV_KEYS,
  DEFAULT_CLIENT_ENV,
  buildFallbackEnv,
  buildClientEnv,
  pickClientEnvVars,
};
